// Periodos.styles.ts
//
// Mesma anatomia das outras sections de Lojas (MeiosPagamento, Ambients):
// FieldsContainer como raiz, Header/HeaderContent/HeaderTitle/HeaderSubtitle,
// LoadingContainer/LoadingSpinner, EmptyState — para a aba nova não parecer de
// outro sistema.
//
// Tamanhos só na escada de tokens (`var(--sp-N)` / `var(--fs-N)`); o que não cai
// nos 13 valores de --sp- vira `calc(var(--u) * N)`. Cores de
// `props.theme.colors`, que hoje só tem background, text, selection,
// modalColor e borderColor — as tonalidades de status ficam em hex literal
// porque o tema não tem chave de sucesso/erro (mesma dívida do Aviso de
// MeiosPagamento).
import styled, { keyframes } from "styled-components";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-24);
  gap: var(--sp-16);
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
`;

export const HeaderContent = styled.div`
  flex: 1;
  min-width: calc(var(--u) * 260);
`;

export const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  margin: 0 0 var(--sp-6) 0;
  font-size: var(--fs-3xl);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  flex-wrap: wrap;
`;

export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-10) var(--sp-20);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-6);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }
`;

// ===== CARTÃO DE UMA LOJA =====
export const LojaCard = styled.div`
  background-color: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  border-radius: 12px;
  padding: var(--sp-20);
  margin-bottom: var(--sp-20);
  animation: ${fadeIn} 0.3s ease;
`;

export const LojaTitulo = styled.h3`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const LojaSubtitulo = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  margin-top: var(--sp-4);
  margin-bottom: var(--sp-16);
`;

// ===== CARTÕES DE PERÍODO DENTRO DA LOJA =====
export const PeriodosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 280), 1fr));
  gap: var(--sp-16);
`;

export const PeriodoCard = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
  border-radius: 8px;
  padding: var(--sp-16);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }
`;

export const PeriodoTituloLinha = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-8);
  margin-bottom: var(--sp-10);
`;

export const PeriodoTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-md);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const StatusBadge = styled.span<{ $ativo?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-2) var(--sp-8);
  border-radius: 12px;
  font-size: var(--fs-xs);
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid ${(props) => (props.$ativo ? "#26792f" : "#be3028")};
  background-color: ${(props) => (props.$ativo ? "#26792f18" : "#be302818")};
  color: ${(props) => props.theme.colors.text};
`;

export const PeriodoMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-6);
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
`;

// ===== AVISO INLINE =====
// Vive na própria aba, e não no <MessageBox /> do Zustand: o MessageBox não está
// montado na tela de Configuração (só em Login e Register), então
// setMessageZustand não apareceria para ninguém.
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  margin-top: var(--sp-16);
  padding: var(--sp-12) var(--sp-16);
  border-radius: 8px;
  font-size: var(--fs-md);
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: var(--sp-10);
  border: 1px solid
    ${(props) =>
      props.$tom === "erro"
        ? "#be3028"
        : props.$tom === "atencao"
        ? "#8f5e07"
        : "#26792f"};
  background-color: ${(props) =>
    props.$tom === "erro"
      ? "#be302818"
      : props.$tom === "atencao"
      ? "#8f5e0718"
      : "#26792f18"};
  color: ${(props) => props.theme.colors.text};
  animation: ${fadeIn} 0.3s ease;
`;

// ===== ESTADOS =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-16);
  padding: calc(var(--u) * 60);
  color: ${(props) => props.theme.colors.label};
`;

export const LoadingSpinner = styled.div`
  width: var(--sp-40);
  height: var(--sp-40);
  border: 4px solid ${(props) => props.theme.colors.selection + "30"};
  border-top-color: ${(props) => props.theme.colors.selection};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

export const EmptyState = styled.div`
  border: 2px dashed ${(props) => props.theme.colors.selection + "50"};
  border-radius: 12px;
  padding: calc(var(--u) * 60) var(--sp-40);
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-7xl);
  margin-bottom: var(--sp-16);
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const EmptyStateTitle = styled.div`
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-8);
`;

export const EmptyStateDescription = styled.div`
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
  margin-bottom: var(--sp-16);
`;

// Um EmptyState menor, para a loja que existe mas ainda não tem período —
// o vazio é DELA, não da aba inteira.
export const LojaSemPeriodos = styled.div`
  border: 1px dashed ${(props) => props.theme.colors.selection + "50"};
  border-radius: 8px;
  padding: var(--sp-20);
  text-align: center;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

export const Contador = styled.div`
  margin-top: var(--sp-16);
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;
